import {
    ModuleAIReviewFinding,
    ModuleAIReviewSection,
} from 'src/services/backend/ModuleAIReviewService'

/**
 * A finding with the 1-based module page it belongs to (undefined when it is module-wide), standing
 * for every finding that differs from it only by locale.
 */
export type ResolvedAIReviewFinding = ModuleAIReviewFinding & {
    pageNumber?: number
    /** The label of the item the entityId names, when the module has one for it. */
    itemLabel?: string
    /** Sorted locales of the findings it stands for; empty when none is locale-specific. */
    locales: string[]
    /** The findings it stands for, one per locale. */
    instances: ModuleAIReviewFinding[]
}

export type ResolvedAIReviewSection = Omit<ModuleAIReviewSection, 'findings'> & {
    findings: ResolvedAIReviewFinding[]
}

const WORKFLOW_PAGE_PATH = /^workflow\[(\d+)\]/

/**
 * Maps every `id` and `label` found anywhere inside a page to that page's 1-based number, so a
 * finding anchored only by entityId (as model findings often are) can still be placed on a page.
 * The first page to claim a value wins.
 */
export function buildEntityPageIndex(workflow: readonly unknown[]): Map<string, number> {
    const index = new Map<string, number>()
    const walk = (value: unknown, pageNumber: number) => {
        if (Array.isArray(value)) {
            value.forEach((child) => walk(child, pageNumber))
            return
        }
        if (!value || typeof value !== 'object') {
            return
        }
        for (const [key, child] of Object.entries(value as Record<string, unknown>)) {
            if ((key === 'id' || key === 'label') && typeof child === 'string' && child) {
                if (!index.has(child)) {
                    index.set(child, pageNumber)
                }
            } else {
                walk(child, pageNumber)
            }
        }
    }
    workflow.forEach((page, i) => walk(page, i + 1))
    return index
}

/**
 * Maps every `id` and `label` found anywhere in the workflow to the label of the object that holds
 * it, so a finding anchored by an item's UUID can be shown with the label authors know it by.
 */
export function buildEntityLabelIndex(workflow: readonly unknown[]): Map<string, string> {
    const index = new Map<string, string>()
    const walk = (value: unknown) => {
        if (Array.isArray(value)) {
            value.forEach(walk)
            return
        }
        if (!value || typeof value !== 'object') {
            return
        }
        const { id, label } = value as Record<string, unknown>
        if (typeof label === 'string' && label) {
            index.set(label, label)
            if (typeof id === 'string' && id) {
                index.set(id, label)
            }
        }
        Object.values(value as Record<string, unknown>).forEach(walk)
    }
    walk(workflow)
    return index
}

/**
 * Resolves the page a finding belongs to. The attributePath is authoritative when it starts with
 * `workflow[N]` (which is how the structural checks anchor findings); otherwise the entityId is
 * looked up among the ids and labels of each page.
 */
export function resolveFindingPageNumber(
    finding: ModuleAIReviewFinding,
    entityPageIndex: Map<string, number>,
    pageCount: number
): number | undefined {
    const match = finding.attributePath ? WORKFLOW_PAGE_PATH.exec(finding.attributePath) : null
    if (match) {
        const pageNumber = Number(match[1]) + 1
        if (pageNumber <= pageCount) {
            return pageNumber
        }
    }
    return finding.entityId ? entityPageIndex.get(finding.entityId) : undefined
}

/** Collapses findings that differ only by locale into one, keeping the order they first appear in. */
export function collapseLocales(
    findings: readonly (ModuleAIReviewFinding & { pageNumber?: number; itemLabel?: string })[]
): ResolvedAIReviewFinding[] {
    const groups = new Map<string, ResolvedAIReviewFinding>()
    for (const finding of findings) {
        const key = JSON.stringify([
            finding.ruleId,
            finding.entityId,
            finding.attributePath,
            finding.pageNumber,
            finding.message,
        ])
        const group = groups.get(key)
        if (group) {
            group.instances.push(finding)
            if (finding.locale && !group.locales.includes(finding.locale)) {
                group.locales.push(finding.locale)
            }
        } else {
            groups.set(key, {
                ...finding,
                locales: finding.locale ? [finding.locale] : [],
                instances: [finding],
            })
        }
    }
    return Array.from(groups.values(), (group) => ({ ...group, locales: group.locales.sort() }))
}

export function resolveSectionPages(
    sections: readonly ModuleAIReviewSection[],
    workflow: readonly unknown[]
): ResolvedAIReviewSection[] {
    const entityPageIndex = buildEntityPageIndex(workflow)
    const entityLabelIndex = buildEntityLabelIndex(workflow)
    return sections.map((section) => ({
        ...section,
        findings: collapseLocales(
            (section.findings ?? []).map((finding) => ({
                ...finding,
                pageNumber: resolveFindingPageNumber(finding, entityPageIndex, workflow.length),
                itemLabel: finding.entityId ? entityLabelIndex.get(finding.entityId) : undefined,
            }))
        ),
    }))
}

/** Sections narrowed to the findings on one page, dropping sections left empty. */
export function sectionsForPage(
    sections: readonly ResolvedAIReviewSection[],
    pageNumber: number
): ResolvedAIReviewSection[] {
    return sections
        .map((section) => ({
            ...section,
            findings: section.findings.filter((f) => f.pageNumber === pageNumber),
        }))
        .filter((section) => section.findings.length > 0)
}
